import { useEffect } from 'react';
import Layout from '../../components/Layout';
import { aboutStyles } from './styles';
import { ReactComponent as WaveSvg } from './images/wave.svg';
import './AboutUs.css';
import { useSubscriptionPrices } from '../../hooks/useSubscriptionPrices';

const assertBaseUrl = process.env.REACT_APP_ASSETS_BASE_URL;
const styles = aboutStyles(assertBaseUrl);

const AboutUs = ({ baseUrl = process.env.REACT_APP_SITE_URL }) => {
  const { seasonalPrice, annualPrice } = useSubscriptionPrices();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');

            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' },
    );

    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <Layout pageTitle="About Us - FabFitFun">
      <div className="pagebody">
        <section className="heroAu">
          <div className="heroAu__background" style={styles['hero-au__background']}>
            <div className="heroAu__container">
              <div className="heroAu__content">
                <h1>
                  <p>Our Mission</p>
                  Delivering Happiness and Well Being to Everyone, Everywhere.
                </h1>
              </div>
            </div>
          </div>
          <div className="heroAu__wave">
            <WaveSvg />
          </div>
        </section>
        <section id="aboutus" className="row container reveal">
          <div className="aboutus">
            <div className="aboutus__wrapper aboutus__wrapper-reverse">
              <div className="aboutus__img">
                <img
                  src={`${process.env.REACT_APP_ASSETS_BASE_URL}/images/fabfitfun-story@2x.png`}
                  alt="Story"
                />
              </div>
              <div className="aboutus__content">
                <h2 className="aboutus__title">The FabFitFun Story</h2>
                <p className="aboutus__text">
                  Founded in 2010 by Co-CEOs Daniel and Michael Broukhim and Editor-in-Chief Katie
                  Echevarria Rosen Kitchens, FabFitFun is a lifestyle membership that is best known
                  for its flagship product, the FabFitFun Box. The FabFitFun box includes a
                  selection of full-size products across beauty, fashion, fitness, wellness, home,
                  and tech – delivered each season. In addition to the Box, FabFitFun members
                  receive access to year-round perks including FabFitFunTV, a streaming video
                  service that offers on-demand wellness content, the FabFitFun online Community,
                  members-only shopping experiences, and more.
                </p>
              </div>
            </div>
          </div>
        </section>
        <div className="about-hr" />
        <section id="leadership" className="leadership container reveal">
          <h2 className="leadership__title">Meet The Founders</h2>
          <div className="leadership__founders--container">
            <div className="leadership__shape--circle shape-circle shape1 visible" />
            <div className="leadership__founders--img">
              <img
                src={`${process.env.REACT_APP_ASSETS_BASE_URL}/images/founders-mobile.png`}
                className="leadership__founders--mobile"
                alt="FabFitFun Founders"
              />
              <img
                src={`${process.env.REACT_APP_ASSETS_BASE_URL}/images/fabfitfun-founders@2x.png`}
                className="leadership__founders--desktop"
                alt="FabFitFun Founders"
              />
            </div>
            <div className="leadership__shape--square shape-square shape2 visible" />
          </div>
          <div>
            <span className="leadership__founders--pictured">Pictured, from left to right</span>
            <div className="leadership__founders">
              <div className="leadership__founder">
                <h4 className="leadership__name">Daniel Broukhim</h4>
                <div className="leadership__position">Co-CEO, Co-Founder</div>
              </div>
              <div className="leadership__founder">
                <h4 className="leadership__name">Katie Rosen Kitchens</h4>
                <div className="leadership__position">Editor in Chief, Co-Founder</div>
              </div>
              <div className="leadership__founder">
                <h4 className="leadership__name">Michael Broukhim</h4>
                <div className="leadership__position">Co-CEO, Co-Founder</div>
              </div>
            </div>
          </div>
        </section>
        <section
          id="fffbox"
          className="fffbox__background reveal"
          style={styles.fffbox__background}
        >
          <div className="row container">
            <div className="fffbox">
              <div className="fffbox__wrapper">
                <div className="fffbox__content">
                  <h2 className="fffbox__title">FabFitFun Box</h2>
                  <p className="fffbox__text">
                    Every season, FabFitFun members receive a selection of full-size products, some
                    of which are curated by our team of experts, while others can be customized
                    based on personal taste and preferences. The FabFitFun Box includes products
                    from both premium and emerging brands. FabFitFun members can subscribe annually
                    for ${annualPrice}
                    /year or seasonally for ${seasonalPrice}
                    /box.
                  </p>
                  <button type="button" className="fff__btn">
                    <a href={baseUrl}>Get The Box</a>
                  </button>
                </div>
                <img
                  src={`${process.env.REACT_APP_ASSETS_BASE_URL}/images/fabfitfun-box@2x.png`}
                  alt="Box"
                  className="fffbox__img"
                />
              </div>
            </div>
          </div>
        </section>
        <section id="membership" className="container reveal">
          <h2 className="membership__title">The Membership</h2>
          <div className="membership__container">
            <div className="membership__perk">
              <div className="membership__perk--icon">
                <img
                  src={`${process.env.REACT_APP_ASSETS_BASE_URL}/images/community@2x.png`}
                  alt="Community"
                  className="membership__img"
                />
              </div>
              <div className="membership__perk--text">
                <h3 className="membership__perk--text-title">Community</h3>
                <p className="membership__perk--text-desc">
                  Connect with members that love FFF as much as you do! Share tips, box faves,
                  recipes and more.
                </p>
              </div>
            </div>
            <div className="membership__perk">
              <div className="membership__perk--icon">
                <img
                  src={`${process.env.REACT_APP_ASSETS_BASE_URL}/images/lifestyle@2x.png`}
                  alt="Lifestyle"
                  className="membership__img"
                />
              </div>
              <div className="membership__perk--text">
                <h3 className="membership__perk--text-title">Daily Lifestyle Content</h3>
                <p className="membership__perk--text-desc">
                  Content delivered across our social media channels, email newsletters, in-box
                  magazine and more.
                </p>
              </div>
            </div>
            <div className="membership__perk">
              <div className="membership__perk--icon">
                <img
                  src={`${process.env.REACT_APP_ASSETS_BASE_URL}/images/perks@2x.png`}
                  alt="Perks"
                  className="membership__img"
                />
              </div>
              <div className="membership__perk--text">
                <h3 className="membership__perk--text-title">Exclusive Member Perks</h3>
                <p className="membership__perk--text-desc">
                  Shop members-only sales with items discounted at up to 70% off!
                </p>
              </div>
            </div>
          </div>
          <div className="fff__btn--wrapper fff__btn--membership">
            <button type="button" className="fff__btn">
              <a href={baseUrl}>Join Now</a>
            </button>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default AboutUs;
